import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LEADERSHIP_NAV, STORE_NAV, FRONTLINE_NAV, filterNav, NavItem,
} from "@/components/shell/nav";
import { LogOut, Menu, X, Search as SearchIcon, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ConnectionBanner } from "@/components/shell/ConnectionBanner";
import { NotificationBell } from "@/components/shell/NotificationBell";
import { PwaStatus } from "@/pwa/PwaStatus";
import { SearchBar } from "@/components/shell/SearchBar";
import { HelpDrawer } from "@/components/shell/HelpDrawer";
import { LanguageSwitcher } from "@/components/shell/LanguageSwitcher";
import { useI18n } from "@/context/I18nContext";
import thShield from "@/assets/th-shield.png";

const roleLabel: Record<string, string> = {
  tenant_admin: "Tenant Administrator",
  corporate_exec: "Corporate Executive",
  regional_manager: "Regional Manager",
  district_manager: "District Manager",
  store_manager: "Store Manager",
  assistant_manager: "Assistant Manager",
  associate: "Associate",
  auditor: "Auditor",
};

const Brand: React.FC<{ dark?: boolean }> = ({ dark }) => {
  const {
    t
  } = useI18n();

  return (
    (<div className="flex items-center gap-2.5" data-testid="app-brand">
      <div className={`grid h-8 w-8 place-items-center rounded-md ${dark ? "bg-brand" : "bg-primary"} text-white font-display font-bold`}>
        S
      </div>
      <div className="leading-tight">
        <div className={`font-display text-sm font-bold ${dark ? "text-sidebar-foreground" : "text-foreground"}`}>{t("StoreCentral")}</div>
        <div className={`text-[10px] uppercase tracking-widest ${dark ? "text-sidebar-muted" : "text-muted-foreground"}`}>{t("WarRoom")}</div>
      </div>
    </div>)
  );
};

const UserFooter: React.FC<{ dark?: boolean }> = ({ dark }) => {
  const {
    t
  } = useI18n();

  const { user, activeMembership, logout } = useAuth();
  const navigate = useNavigate();
  const roles = activeMembership?.roles.map((r) => roleLabel[r] || r).join(", ");
  return (
    (<div className={`border-t p-3 ${dark ? "border-white/10" : "border-border"}`}>
      <div className="mb-2">
        <div className={`text-sm font-semibold ${dark ? "text-sidebar-foreground" : "text-foreground"}`} data-testid="current-user-name">
          {user?.name}
        </div>
        <div className={`text-xs ${dark ? "text-sidebar-muted" : "text-muted-foreground"}`}>{roles}</div>
        {activeMembership && (
          <div className={`mt-0.5 text-[11px] ${dark ? "text-sidebar-muted" : "text-muted-foreground"}`}>
            {activeMembership.tenant_name}
          </div>
        )}
      </div>
      <button
        onClick={async () => { await logout(); navigate("/login"); }}
        data-testid="logout-button"
        className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors ${
          dark ? "text-sidebar-muted hover:bg-white/10 hover:text-white" : "text-muted-foreground hover:bg-secondary"
        }`}
      >
        <LogOut size={16} />{" "}{t("Sign out")}</button>
      <div className="mt-3 flex items-center gap-2 px-1" data-testid="th-company-mark">
        <img src={thShield} alt={t("Titan Haven Group Product")} className="h-5 w-auto opacity-90" />
        <span className={`text-[10px] ${dark ? "text-sidebar-muted" : "text-muted-foreground"}`}>{t("A Titan Haven Group Product")}</span>
      </div>
    </div>)
  );
};

const DesktopSidebar: React.FC<{ items: NavItem[] }> = ({ items }) => {
  const { t } = useI18n();
  const groups = Array.from(new Set(items.map((i) => i.group || "")));
  const groupLabel = (g: string) => { const l = t(`nav.group.${g}`); return l.startsWith("nav.group.") ? g : l; };
  return (
    (<aside className="hidden lg:flex lg:w-64 lg:flex-col bg-sidebar" data-testid="desktop-sidebar">
      <div className="flex items-center justify-between p-4"><Brand dark /><div className="flex items-center gap-0.5"><HelpDrawer dark /><NotificationBell dark /></div></div>
      <div className="px-4 pb-1"><PwaStatus /></div>
      <div className="px-4 pb-2"><SearchBar dark /></div>
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {groups.map((g) => (
          <div key={g} className="mb-4">
            {g && <div className="px-2.5 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted">{groupLabel(g)}</div>}
            {items.filter((i) => (i.group || "") === g).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
                className={({ isActive }) =>
                  `mb-0.5 flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                    isActive ? "bg-sidebar-active text-white font-medium" : "text-sidebar-foreground/80 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <it.icon size={17} />
                <span className="flex-1">{t(it.label)}</span>
                {!it.builtIn && <span className="rounded bg-white/10 px-1.5 py-0.5 text-[9px] uppercase text-sidebar-muted">{t("soon")}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-4 pb-2"><LanguageSwitcher /></div>
      <UserFooter dark />
    </aside>)
  );
};

const MobileBottomNav: React.FC<{ items: NavItem[] }> = ({ items }) => {
  const { t } = useI18n();
  const [moreOpen, setMoreOpen] = useState(false);
  const primary = items.slice(0, 4);
  const hasMore = items.length > 4;
  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] ${isActive ? "text-brand" : "text-muted-foreground"}`;
  return (<>
    <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-card lg:hidden" data-testid="mobile-bottom-nav">
      {primary.map((it) => (
        <NavLink key={it.to} to={it.to} data-testid={`mobnav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`} className={tabClass}>
          <it.icon size={20} />
          <span>{t(it.label)}</span>
        </NavLink>
      ))}
      {hasMore && (
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          data-testid="mobnav-more"
          className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] text-muted-foreground"
        >
          <MoreHorizontal size={20} />
          <span>{t("More")}</span>
        </button>
      )}
    </nav>
    <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
      <SheetContent side="bottom" className="lg:hidden" data-testid="mobile-more-sheet">
        <SheetHeader>
          <SheetTitle>{t("nav.all_destinations")}</SheetTitle>
        </SheetHeader>
        <div className="px-1 pt-2"><LanguageSwitcher /></div>
        <div className="grid grid-cols-3 gap-2 py-4">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={() => setMoreOpen(false)}
              data-testid={`morenav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1.5 rounded-lg border p-3 text-center text-xs ${
                  isActive ? "border-brand/40 bg-accent text-brand" : "border-border text-foreground hover:bg-secondary"
                }`
              }
            >
              <it.icon size={20} />
              <span className="leading-tight">{t(it.label)}</span>
            </NavLink>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  </>);
};

export const AppShell: React.FC = () => {
  const { roleCategory, modules, hasPerm } = useAuth();
  const { t } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);

  const source = roleCategory === "leadership" ? LEADERSHIP_NAV : roleCategory === "store" ? STORE_NAV : FRONTLINE_NAV;
  const items = filterNav(source, modules, hasPerm);

  return (
    <div className="flex min-h-screen bg-background">
      <DesktopSidebar items={items} />

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <ConnectionBanner />
        {/* Mobile / top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-2 overflow-hidden border-b border-border bg-card px-4 py-3 lg:hidden">
          <Brand />
          <div className="flex min-w-0 shrink items-center gap-1">
            <PwaStatus />
            <NavLink to="/search" data-testid="mobile-search-link" className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground hover:bg-secondary">
              <SearchIcon size={18} />
            </NavLink>
            <NotificationBell />
            <HelpDrawer />
            {roleCategory === "leadership" ? (
              <button onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" className="p-1">
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            ) : (
              <span className="rounded-full bg-accent px-2.5 py-1 text-[11px] font-medium capitalize text-accent-foreground">
                {roleCategory}
              </span>
            )}
          </div>
        </header>

        {/* Mobile drawer for leadership */}
        {roleCategory === "leadership" && mobileOpen && (
          <div className="border-b border-border bg-sidebar lg:hidden" data-testid="mobile-drawer">
            <nav className="px-3 py-3">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `mb-0.5 flex items-center gap-3 rounded-md px-2.5 py-2 text-sm ${
                      isActive ? "bg-sidebar-active text-white" : "text-sidebar-foreground/80"
                    }`
                  }
                >
                  <it.icon size={17} /> {t(it.label)}
                </NavLink>
              ))}
            </nav>
            <UserFooter dark />
          </div>
        )}

        <main className={`flex-1 ${roleCategory !== "leadership" ? "pb-20" : ""}`}>
          <div className="mx-auto w-full max-w-6xl px-4 py-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        {roleCategory !== "leadership" && <MobileBottomNav items={items} />}
      </div>
    </div>
  );
};
